import React, { useEffect, useState } from "react";
import AsyncIndicator from "../AsyncIndicator/AsyncIndicator";
import { Button } from "../Button/Button";
import * as ApiHelper from "../../../api/ApiHelper";
import ErrorResponse from "../../../models/ErrorResponse";
import handleErrors from "../../../helpers/ErrorHandler";
import { useAppDispatch } from "../../../hooks";
import {useTranslation} from "react-i18next";
import * as ErrorCodes from "../../../api/ErrorCodes";
import {PasswordBox} from "../InputBox/PasswordBox";

enum Field {
    NewPassword,
    NewPasswordConfirm
}

interface DialogResetPasswordProps {
    token: string;
    userId: string;
    controller: AbortController;
    cancelButtonClickHandler: () => void;
}

export const DialogResetPassword: React.FC<DialogResetPasswordProps> = ({ token, userId, controller, cancelButtonClickHandler }: DialogResetPasswordProps) => {
    const {t} = useTranslation();
    const dispatch = useAppDispatch();
    const [badField, setBadField] = useState(null as Field | null);
    const [newPassword, setNewPassword] = useState('');
    const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
    const handleNewPasswordChange = (value: string) => setNewPassword(value);
    const handleNewPasswordConfirmChange = (value: string) => setNewPasswordConfirm(value);
    const [isFetching, setFetchingState] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null as string | null);
    const [dialogTop, setDialogTop] = useState(document.documentElement.scrollTop + document.documentElement.clientHeight * 0.25);
    const scrollHandler = () => {
        setDialogTop(document.documentElement.scrollTop + document.documentElement.clientHeight * 0.25);
    };
    useEffect(() => {
        window.addEventListener('scroll', scrollHandler);
        return () => {
            window.removeEventListener('scroll', scrollHandler)
        }
    });
    const keyDownHandler = (e : KeyboardEvent) => {
        if (e.key === "Enter"){
            handleChangeButtonClick();
        }
    };
    useEffect(() => {
        window.addEventListener('keydown', keyDownHandler);
        return () => {
            window.removeEventListener('keydown', keyDownHandler);
        }
    });
    const resetHandler = () => {
        setFetchingState(false);
        setErrorMessage(null);
        cancelButtonClickHandler();
    };
    const errorHandler = (error: ErrorResponse) => {
        if (!handleErrors(error, dispatch)) {
            if (error.ResultCode === ErrorCodes.BadPassword) {
                setErrorMessage(t("incorrect_password_full"));
                setBadField(Field.NewPassword);
            } else {
                setErrorMessage(t("connection_problem"));
                setBadField(null);
            }
            setFetchingState(false);
        }
    };
    const handleChangeButtonClick = () => {       
        if (newPassword.trim() === '') {
            setBadField(Field.NewPassword);
            setErrorMessage(t("enter_password"));
            return;
        }
        if (newPassword.trim() !== newPasswordConfirm.trim()) {
            setBadField(Field.NewPasswordConfirm);
            setErrorMessage(t("password_mismatch"));
            return;
        }
        setBadField(null);
        setErrorMessage(null);
        setFetchingState(true);
        ApiHelper.resetPassword(token, userId, newPassword, controller, resetHandler, errorHandler)
    };
    const documentHeight = document.documentElement.offsetHeight;
    return (
        <div className="dialog-container" style={{ height: documentHeight }}>
            {isFetching ? (
                <div className="window-modal" style={{ marginTop: dialogTop }}>
                    <AsyncIndicator />
                </div>
            ) : (
                <div className="window-modal" style={{ marginTop: dialogTop }}>

                    <div className="window-modal-title disable-select pb-4 d-flex justify-content-center">{t("change_password_title")}</div>
                    <div className="window-modal-error d-flex justify-content-center align-items-center my-2">
                        {errorMessage ? errorMessage : ""}
                    </div>
                    <PasswordBox className="my-2" title={t("new_password")} initialValue={newPassword} hasError={badField === Field.NewPassword} onChange={handleNewPasswordChange} />
                    <PasswordBox className="my-2" title={t("confirm_password")} initialValue={newPasswordConfirm} hasError={badField === Field.NewPasswordConfirm} onChange={handleNewPasswordConfirmChange} />
                    <div className="d-flex justify-content-around">
                        <Button className="mt-4" text={t("change")} danger={true} onClick={handleChangeButtonClick} />
                        <Button className="mt-4" text={t("cancel")} onClick={cancelButtonClickHandler} />
                    </div>
                </div>)
            }
        </div >
    );
}